import * as React from "react";
import { Copy24Regular } from "@fluentui/react-icons";
import { AuthError, Organization } from "./OrganizationModel";
import { TableBody, TableCell, TableRow, Table, TableHeader, Button } from "@fluentui/react-components";
import { useLanguage } from "../../common/i18n/i18n";
import {
  Dropdown,
  DropdownMenuItemType,
  IDropdownOption,
  IDropdownStyles,
  IStackTokens,
  Link,
  Stack,
  Spinner,
} from "@fluentui/react";
import { useAuthorization } from "../authentication/AuthorizationContext";
import { CollectionInstanceRequest, Collections } from "../userAssets/CollectionModel";
import CollectionAPI from "../api/CollectionAPI";
import { useUserAsset } from "../userAssets/UserAssetContext";
import { DownloadIcon } from "@fluentui/react-icons-mdl2";
import WebScraperAPI, { RepresentativeEnum } from "../api/WebSrcaperAPI";
import OrganizationError from "./OrganizationError";
import { isFeatureVisible } from "../../common/utils/featureStatus";

// eslint-disable-next-line @typescript-eslint/no-unused-vars
/* global document, Office, Word, module, require, console, navigator, btoa, window */

const PDF_MIME_TYPE = "application/pdf";
interface OrgTabProps {
  organization: Organization;
  isDate: boolean;
}
interface DropdownOptionItem {
  key: string;
  text: string;
}
interface DropdownOptionItems extends Array<DropdownOptionItem> {}
interface Item {
  key: string;
  value: string;
}
const itemTokens = [
  "name",
  "krs",
  "nip",
  "regon",
  "headquater",
  "address",
  "courtOfLaw",
  "wayOfRepresentation",
  "source",
];

const transformations = {
  address: (obj: any) => {
    return `${obj?.postalCode} ${obj?.city}, ${obj?.street ? obj?.street : obj?.city} ${obj?.streetNumber} ${
      obj?.apartment ? "lok. " + obj?.apartment : ""
    }`;
  },
  headquaterKRS: (obj: any) => {
    return `${obj?.voivodeship}, ${obj?.community}`;
  },
  headquaterCEIDG: (obj: any) => {
    return `${obj?.postalCode} ${obj?.city}, ${obj?.street ? obj?.street : obj?.city} ${obj?.streetNumber} ${
      obj?.apartment ? "lok. " + obj?.apartment : ""
    }`;
  },
  default: (str: string) => {
    return str;
  },
};

const dropdownTokens: IStackTokens = {
  childrenGap: 8,
  maxWidth: "200px",
};
const dropdownStyles: Partial<IDropdownStyles> = {
  dropdown: { width: 150 },
};

function getDropdownLabels(collections: Collections): DropdownOptionItems {
  let result: DropdownOptionItems = [];
  if (collections) {
    result = collections.flatMap((item) => {
      return {
        key: item.id,
        text: item.metadata.uiprops.label ? item.metadata.uiprops.label : item.metadata.collectionName,
      };
    });
  }
  return result;
}

const OrganizationData: React.FC<OrgTabProps> = ({ organization, isDate }) => {
  const { isAuthorized, authorizedFeatures } = useAuthorization();
  const [isDownloading, setIsDownloading] = React.useState<boolean>(false);
  const { pickedCollection, collections, populatePickedCollection } = useUserAsset();
  const [errorMessage, setErrorMessage] = React.useState<AuthError>(null);
  const { labels } = useLanguage();
  // eslint-disable-next-line no-undef
  const isOfficeOnline: boolean = localStorage.getItem("isOfficeOnline") === "true";

  const options: IDropdownOption[] = [
    {
      key: "pickedHeader",
      text: labels.LABELS_I18N.organizationPage.dropdown.dropdownOptions.pickedHeader,
      itemType: DropdownMenuItemType.Header,
    },
    {
      key: pickedCollection?.id,
      text: pickedCollection?.metadata.collectionName,
    },
    {
      key: "availableHeader",
      text: labels.LABELS_I18N.organizationPage.dropdown.dropdownOptions.availableHeader,
      itemType: DropdownMenuItemType.Header,
    },
    ...getDropdownLabels(collections),
  ];

  function transformToItems(organization: Organization): Item[] {
    let map = [];
    if (organization != null) {
      itemTokens.forEach((item) => {
        let t: { key: string; value: string };
        let strategy = item + organization.source;
        switch (item) {
          case "address":
            t = {
              key: labels.LABELS_I18N.organizationData.rowLabels[item],
              value: transformations.address(organization[item]),
            };
            break;
          case "headquater":
            t = {
              key: labels.LABELS_I18N.organizationData.rowLabels[item],
              value: transformations[strategy](organization[item]),
            };
            break;
          default:
            t = {
              key: labels.LABELS_I18N.organizationData.rowLabels[item],
              value: transformations.default(organization[item]),
            };
            break;
        }
        if (!(t.value == null || typeof t.value == "undefined")) {
          map.push(t);
        }
      });
    }
    return map;
  }

  const handleInsertParagraph = async (data) => {
    let requestData: CollectionInstanceRequest = {
      parameters: {
        companyName: data.name,
        nipId: data.nip,
        krsId: data.krs,
        regonId: data.regon,
        companyAddressCity: data.address.city,
        companyHeadquaterCity: data.address.city,
        companyAddressCountry: data.address.country,
        companyAddressStreet: data.address.street,
        companyAddressStreetNumber: data.address.streetNumber,
        companyAddressApartment: data.address.apartment,
        companyAddressPostalCode: data.address.postalCode,
        courtOfLaw: data.courtOfLaw,
        companyFund: data.companyFund,
        companyFundCurrency: data.companyFundCurrency,
        companyFundPaid: data.companyFundPaid,
        companyFundPaidCurrency: data.companyFundPaidCurrency,
        companyRegisterFlag: data.companyRegisterFlag,
        companyOwnerFirstName: data.ownerFirstName,
        companyOwnerLastName: data.ownerLastName,
      },
    };

    let paragraphInsertData = await CollectionAPI.createCollectionTemplateInstance(
      requestData,
      pickedCollection.id,
      data.templateName
    )
      .then((response) => {
        return {
          data: response.json(),
          status: response.status,
        };
      })
      .then((res) => {
        if (res.status !== 201) {
          throw new Error("Could not fetch collection template instance");
        } else {
          return res.data.then((templateInstance) => {
            return templateInstance;
          });
        }
      });

    await Word.run(async (context) => {
      let body = context.document.getSelection();
      body.insertText(paragraphInsertData?.rawText.run, Word.InsertLocation.after);
      await context.sync();
    });
  };
  const handlePickedCollection = (_event: React.FormEvent<HTMLDivElement>, item: IDropdownOption): void => {
    populatePickedCollection(item.key as string);
  };

  const handleCopyClick = (value: string) => () => {
    if (!isOfficeOnline) navigator.clipboard.writeText(value);
  };

  const items = transformToItems(organization);

  function arrayBufferToBase64(buffer: ArrayBuffer) {
    let binary = "";
    const bytes = new Uint8Array(buffer);
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }

  function handleDownloadCopy() {
    setIsDownloading(true);

    let organizationType = organization.companyRegisterFlag
      ? RepresentativeEnum[RepresentativeEnum.enterprise]
      : RepresentativeEnum[RepresentativeEnum.associacion];

    WebScraperAPI.getActualCopy(organization.krs, organizationType)
      .then((res) => {
        let pdfBodyBase64 = null;
        let filename = null;
        let childMessage: string;
        if (res.status != 200) {
          console.log(res.statusText);
          setErrorMessage({
            response: {
              status: res.status,
              data: {
                code: 900,
                message: res.statusText,
              },
            },
          });
        } else {
          setErrorMessage(null);
          filename = `Odpis_Aktualny_${organization.krs}.pdf`;
          res
            .arrayBuffer()
            .then((body) => {
              pdfBodyBase64 = arrayBufferToBase64(body);
              childMessage = `data:${PDF_MIME_TYPE};filename=${filename};base64,${pdfBodyBase64}`;
            })
            .catch((err) => {
              console.log(err);
              setIsDownloading(false);
            });
          Office.context.ui.displayDialogAsync(
            `${window.location.origin}/dialog.html`, // Replace with the URL of your dialog page
            { height: 80, width: 50 },
            function (asyncResult) {
              if (asyncResult.status === Office.AsyncResultStatus.Succeeded) {
                let dialog = asyncResult.value;
                dialog.addEventHandler(Office.EventType.DialogMessageReceived, (message) => {
                  if ("message" in message) {
                    let msg = JSON.parse(message.message);
                    if (msg?.status == "loaded") {
                      // delay message to make sure dialog box event listener is initialised.
                      // eslint-disable-next-line no-undef
                      setTimeout(() => {
                        dialog.messageChild(childMessage);
                      }, 1000);
                    } else {
                      dialog.close();
                      setIsDownloading(false);
                    }
                  } else {
                    dialog.close();
                    setIsDownloading(false);
                  }
                });
                dialog.addEventHandler(Office.EventType.DialogEventReceived, (event) => {
                  if ("error" in event && event.error != 12006)
                    console.error("Dialog closed or error occurred:", event);
                  setIsDownloading(false);
                });
              } else {
                console.error("Failed to open dialog:", asyncResult.error.message);
                setIsDownloading(false);
              }
            }
          );
        }
      })
      .catch((err) => {
        console.log(err);
        setErrorMessage({
          response: {
            status: err.status,
            data: {
              code: err.code,
              message: err.message,
            },
          },
        });
        setIsDownloading(false);
      });
  }

  return (
    <React.Fragment>
      {errorMessage != null && <OrganizationError error={errorMessage} />}
      <Table style={{ maxWidth: "100%" }}>
        <TableHeader />
        <TableBody>
          {items.map((item) => (
            <TableRow key={item.key} onClick={handleCopyClick(item.value)}>
              <TableCell>{item.key}</TableCell>
              <TableCell>{item.value}</TableCell>
              {!isOfficeOnline && (
                <TableCell>
                  <Link title={labels.LABELS_I18N.organizationData.copied.title} onClick={handleCopyClick(item.value)}>
                    <Copy24Regular />
                    {labels.LABELS_I18N.organizationData.copied.label}
                  </Link>
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {isAuthorized && (
        <React.Fragment>
          <div className="blink-actions">
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center" tokens={dropdownTokens}>
              <Stack.Item>
                <Button onClick={() => handleInsertParagraph(organization)} appearance="primary">
                  {labels.LABELS_I18N.organizationPage.primaryButton.text}
                </Button>
              </Stack.Item>
              <Stack.Item>
                <Dropdown
                  placeholder={labels.LABELS_I18N.organizationPage.dropdown.placeholder}
                  options={options}
                  styles={dropdownStyles}
                  onChange={handlePickedCollection}
                />
              </Stack.Item>
              {isFeatureVisible(authorizedFeatures, "download_pdf") && (
                <Stack.Item>
                  {!isDownloading &&
                    items.filter((source) => source.key == labels.LABELS_I18N.organizationData.rowLabels.source).pop()
                      ?.value == "KRS" && (
                      <Button
                        onClick={() => {
                          handleDownloadCopy();
                        }}
                        icon={<DownloadIcon />}
                        appearance="secondary"
                        disabled={isDate}
                        title={
                          isDate
                            ? labels.LABELS_I18N.organizationData.buttons.downloadButtonDisabledTitle
                            : labels.LABELS_I18N.organizationData.buttons.downloadButtonEnabledTitle
                        }
                      >
                        {labels.LABELS_I18N.organizationPage.downloadButton.text}
                      </Button>
                    )}
                  {isDownloading && <Button disabled={true}>{<Spinner />}</Button>}
                </Stack.Item>
              )}
            </Stack>
          </div>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default OrganizationData;
